/*****************************************************
Naam: CustomerHelpCenter.jsx
Locatie: src/pages/CustomerDashboard/CustomerHelpCenter.jsx
Datum: 2026-03-24
Versie: 1.0.0
******************************************************
Beschrijving:
Helpcenter voor klanten — zoekbaar kennisbank met
categorieën, artikelen en FAQ.
******************************************************/

import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../LanguageContext';

// ─── DATA ────────────────────────────────────────────────────────────────────

const CATEGORIES_DATA = [
  { id:'aanvraag',   icon:'📦', kleur:'#1a364c', tk:'help_aanvraag',   sk:'help_s_aanvraag' },
  { id:'tracking',   icon:'📡', kleur:'#0e7490', tk:'help_tracking',   sk:'help_s_tracking' },
  { id:'berichten',  icon:'💬', kleur:'#166534', tk:'help_berichten',  sk:'help_s_berichten' },
  { id:'documenten', icon:'📄', kleur:'#92400e', tk:'help_documenten', sk:'help_s_documenten' },
  { id:'account',    icon:'👤', kleur:'#4c1d95', tk:'help_account',    sk:'help_s_account' },
  { id:'faq',        icon:'❓', kleur:'#be185d', tk:'help_faq',        sk:'help_s_faq' },
];
const getCategories = (t) => CATEGORIES_DATA.map(c => ({
  ...c,
  titel: t(c.tk) || c.tk,
  omschrijving: t(c.sk) || c.sk,
}));

const ARTIKELEN = [
  // ── AANVRAAG ──────────────────────────────────────────────────────────────
  {
    id: 'a1',
    categorie: 'aanvraag',
    titel: 'Een nieuwe transportaanvraag indienen',
    inhoud: `
## Een nieuwe transportaanvraag indienen

Via het klantportaal van SendWell kunt u eenvoudig een transportaanvraag indienen. Volg de onderstaande stappen:

### Stap 1 — Open het aanvraagformulier
Klik rechtsboven op **"Nieuwe aanvraag"**. Het aanvraagformulier opent zich in uw scherm.

### Stap 2 — Vul de laad- en losgegevens in
Vul het **laadadres** (vertrekpunt) en het **losadres** (bestemming) in, inclusief postcode, plaatsnaam en land. Voeg ook de gewenste **laaддatum** toe.

### Stap 3 — Omschrijf uw goederen
Geef per collo de volgende informatie op:
- Aantal en soort (bijv. europallet, colli)
- Omschrijving van de goederen
- Afmetingen (lengte × breedte × hoogte in meters)
- Gewicht per eenheid (in kg)
- Stapelbaar: ja of nee

### Stap 4 — Bijzonderheden
Heeft u bijzondere eisen? Geef dit aan bij **Specialiteiten**:
- ADR (gevaarlijke stoffen)
- Geconditioneerd transport (koel/vries)
- Groupage of full truck load

### Stap 5 — Versturen
Klik op **"Aanvraag versturen"**. SendWell ontvangt uw aanvraag en neemt zo snel mogelijk contact op met een prijsopgave.

> **Tip:** U ontvangt een bevestiging via het berichten-systeem zodra uw aanvraag is verwerkt.
    `,
  },
  {
    id: 'a2',
    categorie: 'aanvraag',
    titel: 'Wat gebeurt er na het indienen van mijn aanvraag?',
    inhoud: `
## Wat gebeurt er na het indienen van mijn aanvraag?

Na het indienen doorloopt uw aanvraag de volgende stappen:

### 1. Ontvangstbevestiging
U ontvangt een automatische bevestiging zodra uw aanvraag is ontvangen. De status verandert naar **"Prijzen nog aan te vragen"**.

### 2. Prijsaanvraag bij vervoerders
SendWell vraagt prijzen op bij geselecteerde vervoerders die geschikt zijn voor uw route en vereisten.

### 3. Prijsopgave
Zodra de prijzen zijn ontvangen, stelt SendWell een **prijsopgave** op. U ontvangt een melding via het berichtensysteem.

### 4. Opdracht bevestigen
Gaat u akkoord met de offerte? Klik op **"Opdracht bevestigen"** in uw portaal. U ontvangt een opdrachtbevestiging met alle details.

### 5. Transport en tracking
Na bevestiging wordt de tracker aan uw zending gekoppeld. U kunt de zending live volgen via het **Tracking** tabblad.

### Statussen uitgelegd

| Status | Betekenis |
|--------|-----------|
| Prijzen nog aan te vragen | Aanvraag ontvangen, vervoerders worden benaderd |
| Prijzen uitgezet | Prijsaanvragen zijn verstuurd |
| Prijzen vervoerders binnen | Offerte wordt opgesteld |
| Prijs geoffreerd | Offerte klaar, wacht op uw akkoord |
| Opdracht | Opdracht bevestigd, transport gepland |
| Afgerond | Transport succesvol uitgevoerd |
    `,
  },
  {
    id: 'a3',
    categorie: 'aanvraag',
    titel: 'Kan ik een aanvraag wijzigen of annuleren?',
    inhoud: `
## Aanvraag wijzigen of annuleren

### Wijzigen
Na het indienen kunt u een aanvraag **niet meer zelf wijzigen** via het portaal. Neem contact op via het **berichtensysteem** om een wijziging door te geven. SendWell past de aanvraag dan voor u aan.

### Annuleren
Wilt u een aanvraag annuleren voordat de opdracht is bevestigd? Stuur een bericht via het portaal met het aanvraagnummer en de reden. SendWell verwerkt de annulering zo snel mogelijk.

### Na opdrachtbevestiging
Is de opdracht al bevestigd? Dan gelden de annuleringsvoorwaarden zoals vermeld in uw overeenkomst. Neem contact op voor de mogelijkheden.

> **Let op:** Bij annulering na opdrachtbevestiging kunnen annuleringskosten in rekening worden gebracht.
    `,
  },

  // ── TRACKING ──────────────────────────────────────────────────────────────
  {
    id: 't1',
    categorie: 'tracking',
    titel: 'Hoe volg ik mijn zending live?',
    inhoud: `
## Live tracking van uw zending

SendWell maakt gebruik van GPS-trackers die op uw zending worden geplaatst. Via het portaal kunt u de locatie in real-time volgen.

### Stap 1 — Ga naar Tracking
Klik in het menu op **"Tracking"**. U ziet een kaart met alle actieve zendingen.

### Stap 2 — Selecteer uw zending
In de linkerkolom ziet u een lijst van uw zendingen. Klik op een zending om de route op de kaart te zien.

### Wat ziet u?
- **Huidige locatie** — de laatste bekende positie van de tracker
- **Route** — de afgelegde weg van laadadres naar huidig punt
- **Snelheid** — de actuele rijsnelheid
- **Batterij** — batterijniveau van de tracker
- **Laatste update** — tijdstip van de meest recente positie

### Hoe vaak wordt de positie bijgewerkt?
De tracker stuurt elke **30 tot 40 seconden** een nieuwe positie. Bij stilstand wordt de update minder frequent.

### Geen data zichtbaar?
- Controleer of de zending de status **"Opdracht"** heeft
- De tracker heeft buiten een GPS-signaal nodig
- In tunnels of parkeergarages kan het signaal tijdelijk wegvallen
    `,
  },
  {
    id: 't2',
    categorie: 'tracking',
    titel: 'Routegeschiedenis bekijken',
    inhoud: `
## Routegeschiedenis bekijken

Naast live tracking kunt u ook de volledige routegeschiedenis van een zending bekijken.

### Hoe?
1. Open het **Tracking** tabblad
2. Klik op de gewenste zending
3. Klik op **"Meer info"** onder de zendingdetails
4. Selecteer een datumbereik om de route van die dag te zien

### Wat kunt u terugvinden?
- Exacte route per dag
- Tussentijdse stops
- Snelheidsverloop
- Batterijverloop

### Hoe lang wordt de geschiedenis bewaard?
De routegeschiedenis wordt bewaard voor de duur van de opdracht plus **30 dagen** na afronding.
    `,
  },

  // ── BERICHTEN ─────────────────────────────────────────────────────────────
  {
    id: 'b1',
    categorie: 'berichten',
    titel: 'Hoe stuur ik een bericht naar SendWell?',
    inhoud: `
## Berichten sturen via het portaal

Het berichtensysteem is de snelste manier om contact op te nemen met SendWell.

### Bericht sturen
1. Klik in het menu op **"Berichten"**
2. Klik op **"Nieuw bericht"** of open een bestaand gesprek
3. Typ uw bericht in het tekstvak onderin
4. Druk op **Versturen** of gebruik Enter

### Koppelen aan een aanvraag
U kunt een bericht koppelen aan een specifieke aanvraag. Selecteer het aanvraagnummer in het dropdown-menu boven het tekstvak. Zo blijft alle communicatie overzichtelijk per dossier.

### Bijlagen
U kunt ook **foto's en afbeeldingen** meesturen via het paperclip-icoon. Handig bij schademeldingen of vragen over uw goederen.

### Reactietijd
SendWell streeft ernaar binnen **4 uur** te reageren tijdens kantooruren (ma–vr 08:00–17:30).

> **Tip:** Voor urgente zaken kunt u ook telefonisch contact opnemen via **085 – 800 1843**.
    `,
  },
  {
    id: 'b2',
    categorie: 'berichten',
    titel: 'Ik heb een melding ontvangen — wat nu?',
    inhoud: `
## Meldingen en notificaties

### Wat zijn meldingen?
SendWell stuurt u meldingen bij belangrijke updates, zoals:
- Een nieuwe prijsopgave is klaar
- Uw zending heeft de bestemming bereikt
- SendWell heeft gereageerd op uw bericht

### Waar zie ik meldingen?
Meldingen verschijnen rechtsboven in het portaal als een badge op het bericht-icoon.

### E-mail notificaties
Belangrijke updates worden ook naar uw geregistreerde e-mailadres gestuurd. Controleer eventueel uw spammap als u geen e-mails ontvangt.
    `,
  },

  // ── DOCUMENTEN ────────────────────────────────────────────────────────────
  {
    id: 'd1',
    categorie: 'documenten',
    titel: 'Waar vind ik mijn offertes en opdrachtbevestigingen?',
    inhoud: `
## Offertes en opdrachtbevestigingen

### Offerte inzien
1. Open de aanvraag waarvoor u een offerte heeft ontvangen
2. Klik op het tabblad **"Documenten"** of scroll naar het prijsoverzicht
3. De offerte toont de all-in prijs exclusief BTW

### Opdrachtbevestiging
Na akkoord op de offerte ontvangt u een **opdrachtbevestiging** per e-mail. U kunt deze ook terugvinden in het portaal:
1. Open de betreffende aanvraag
2. Klik op **"Opdrachtbevestiging"**
3. U kunt het document afdrukken via de afdruk-knop

### Wat staat er in de opdrachtbevestiging?
- Laad- en losadres
- Datum en tijdvenster
- Omschrijving van de goederen
- Overeengekomen prijs
- Trackerinformatie (indien van toepassing)
    `,
  },
  {
    id: 'd2',
    categorie: 'documenten',
    titel: 'Hoe kan ik een document downloaden of afdrukken?',
    inhoud: `
## Documenten downloaden en afdrukken

### Afdrukken
Elk document in het portaal heeft een **"Afdrukken"** knop. Klik hierop om uw browser afdrukdialoog te openen. U kunt ook opslaan als PDF via uw printer-instellingen.

### Downloaden als PDF
Via de afdrukfunctie kiest u bij de printer **"Opslaan als PDF"** om een digitale kopie op te slaan.

### Documenten niet zichtbaar?
- Controleer of de aanvraag de juiste status heeft
- Neem contact op via het berichtensysteem als een document ontbreekt
    `,
  },

  // ── ACCOUNT ───────────────────────────────────────────────────────────────
  {
    id: 'ac1',
    categorie: 'account',
    titel: 'Hoe log ik in op het portaal?',
    inhoud: `
## Inloggen op het SendWell portaal

### Inloggen
1. Ga naar **dashboard.sendwell.nl/portal**
2. Voer uw **e-mailadres** en **wachtwoord** in
3. Klik op **"Inloggen"**

### Wachtwoord vergeten?
Neem contact op via **info@sendwell.nl** of bel **085 – 800 1843**. SendWell stuurt u een nieuw wachtwoord toe.

### Eerste keer inloggen?
U ontvangt van SendWell een e-mail met uw inloggegevens zodra uw account is aangemaakt. Controleer uw spammap als u geen e-mail heeft ontvangen.

### Problemen met inloggen?
- Controleer of u het juiste e-mailadres gebruikt
- Controleer of Caps Lock aan staat
- Wis uw browsercache en probeer opnieuw
- Neem contact op met SendWell als het probleem aanhoudt
    `,
  },
  {
    id: 'ac2',
    categorie: 'account',
    titel: 'Wie kan het portaal gebruiken binnen mijn bedrijf?',
    inhoud: `
## Portaaltoegang voor uw bedrijf

### Toegang aanvragen
Het portaal is toegankelijk voor medewerkers van uw bedrijf die zijn geregistreerd bij SendWell. Wilt u extra gebruikers toevoegen? Neem contact op via het berichtensysteem of per e-mail.

### Wat zien andere gebruikers?
Alle gebruikers van uw bedrijf zien dezelfde aanvragen en berichten. Er is geen onderscheid in toegangsniveaus.

### Uitloggen
Klik rechtsboven op uw naam of het account-icoon en kies **"Uitloggen"**. Op gedeelde computers raden wij aan altijd uit te loggen na gebruik.
    `,
  },
];

const FAQ_ITEMS = [
  {
    vraag: 'Hoe snel ontvangt SendWell mijn aanvraag?',
    antwoord: 'Uw aanvraag wordt direct na het versturen ontvangen door SendWell. Tijdens kantooruren (ma–vr 08:00–17:30) ontvangt u doorgaans binnen 4 uur een reactie.',
  },
  {
    vraag: 'Kan ik meerdere zendingen tegelijk volgen?',
    antwoord: 'Ja. In het Tracking-overzicht ziet u alle actieve zendingen op één kaart. U kunt per zending in- en uitzoomen op de route.',
  },
  {
    vraag: 'Wat doe ik bij schade aan mijn zending?',
    antwoord: 'Meld schade zo snel mogelijk via het berichtensysteem. Voeg foto\'s toe van de beschadigde goederen en de verpakking. SendWell neemt dan contact op om de vervolgstappen te bespreken.',
  },
  {
    vraag: 'Werkt het portaal ook op mijn telefoon?',
    antwoord: 'Ja, het portaal is volledig responsief en werkt op smartphones en tablets. U heeft alleen een internetbrowser nodig, geen app.',
  },
  {
    vraag: 'Hoe weet ik wanneer mijn zending is afgeleverd?',
    antwoord: 'U ontvangt een melding zodra de zending als "Afgerond" is gemarkeerd. Via Tracking kunt u ook de laatste bekende positie en het tijdstip van aflevering terugzien.',
  },
  {
    vraag: 'Kan ik een tracker ook meteen meegeven aan de chauffeur?',
    antwoord: 'De tracker wordt door SendWell op de zending geplaatst voorafgaand aan het transport. U hoeft hier zelf niets voor te regelen.',
  },
  {
    vraag: 'Wat is het verschil tussen groupage en full truck?',
    antwoord: 'Bij groupage deelt uw zending de vrachtwagen met andere klanten — ideaal voor kleinere volumes. Bij full truck (FTL) heeft u de volledige vrachtwagen exclusief voor uw zending, wat sneller en geschikter is voor grotere ladingen.',
  },
  {
    vraag: 'Hoe lang duurt het voordat ik een prijsopgave ontvang?',
    antwoord: 'SendWell streeft ernaar binnen 4 uur een prijsopgave te sturen. Voor complexe routes of speciale transporten (ADR, koel/vries) kan dit iets langer duren.',
  },
  {
    vraag: 'Kan ik ook buiten kantooruren berichten sturen?',
    antwoord: 'Ja, u kunt altijd berichten sturen via het portaal. SendWell reageert de eerstvolgende werkdag tijdens kantooruren.',
  },
  {
    vraag: 'Zijn mijn gegevens veilig in het portaal?',
    antwoord: 'Ja. Het portaal is beveiligd met een versleutelde HTTPS-verbinding. Uw gegevens worden opgeslagen op Europese servers en verwerkt conform de AVG/GDPR.',
  },
];

// ─── COMPONENT ───────────────────────────────────────────────────────────────
// ─── Volledige artikel-inhoud vertalingen ────────────────────────────────────
const INHOUD_VERTALINGEN = {
  'a1': {
    en: `## Submit a new transport request

Through the SendWell customer portal, you can easily submit a transport request. Follow these steps:

### Step 1 — Open the request form
Click **"New shipment"** in the top right. The request form will open on your screen.

### Step 2 — Fill in loading and delivery details
Enter the **loading address** (origin) and the **delivery address** (destination), including postcode, city and country. Also add the desired **loading date**.

### Step 3 — Describe your goods
For each package, provide the following information:
- Quantity and type (e.g. euro pallet, parcel)
- Description of the goods
- Dimensions (length × width × height in metres)
- Weight per unit (in kg)
- Stackable: yes or no

### Step 4 — Special requirements
Do you have special requirements? Enter them under **Special requirements**:
- ADR (dangerous goods)
- Temperature-controlled transport (refrigerated/frozen)
- Groupage or full truck load

### Step 5 — Submit
Click **"Submit request"**. SendWell will receive your request and contact you as soon as possible with a quote.

> **Tip:** You will receive a confirmation via the messaging system once your request has been processed.`,
    de: `## Eine neue Transportanfrage stellen

Über das SendWell-Kundenportal können Sie einfach eine Transportanfrage einreichen. Folgen Sie diesen Schritten:

### Schritt 1 — Anfrageformular öffnen
Klicken Sie oben rechts auf **"Neue Sendung"**. Das Anfrageformular öffnet sich auf Ihrem Bildschirm.

### Schritt 2 — Lade- und Lieferdaten eingeben
Geben Sie die **Ladeadresse** (Abfahrtsort) und die **Lieferadresse** (Ziel) ein, einschließlich PLZ, Ort und Land. Fügen Sie auch das gewünschte **Ladedatum** hinzu.

### Schritt 3 — Waren beschreiben
Geben Sie für jedes Paket die folgenden Informationen an:
- Menge und Art (z.B. Europalette, Paket)
- Beschreibung der Waren
- Abmessungen (Länge × Breite × Höhe in Metern)
- Gewicht pro Einheit (in kg)
- Stapelbar: ja oder nein

### Schritt 4 — Besondere Anforderungen
Haben Sie besondere Anforderungen? Geben Sie diese unter **Besonderheiten** an:
- ADR (Gefahrgut)
- Temperaturgeführter Transport (Kühl/Gefrierware)
- Sammelladung oder Vollladung

### Schritt 5 — Absenden
Klicken Sie auf **"Anfrage senden"**. SendWell erhält Ihre Anfrage und meldet sich schnellstmöglich mit einem Angebot.

> **Tipp:** Sie erhalten eine Bestätigung über das Nachrichtensystem, sobald Ihre Anfrage bearbeitet wurde.`,
    fr: `## Soumettre une nouvelle demande de transport

Via le portail client SendWell, vous pouvez facilement soumettre une demande de transport. Suivez ces étapes :

### Étape 1 — Ouvrir le formulaire de demande
Cliquez sur **"Nouvelle expédition"** en haut à droite. Le formulaire de demande s'ouvrira sur votre écran.

### Étape 2 — Remplir les informations de chargement et livraison
Entrez l\\'**adresse de chargement** (origine) et l\\'**adresse de livraison** (destination), y compris le code postal, la ville et le pays. Ajoutez également la **date de chargement** souhaitée.

### Étape 3 — Décrire vos marchandises
Pour chaque colis, fournissez les informations suivantes :
- Quantité et type (ex. palette européenne, colis)
- Description des marchandises
- Dimensions (longueur × largeur × hauteur en mètres)
- Poids par unité (en kg)
- Gerbable : oui ou non

### Étape 4 — Exigences spéciales
Avez-vous des exigences particulières ? Indiquez-les sous **Exigences spéciales** :
- ADR (matières dangereuses)
- Transport à température contrôlée (réfrigéré/congelé)
- Groupage ou chargement complet

### Étape 5 — Envoyer
Cliquez sur **"Envoyer la demande"**. SendWell recevra votre demande et vous contactera dans les plus brefs délais avec un devis.

> **Conseil :** Vous recevrez une confirmation via le système de messagerie dès que votre demande aura été traitée.`,
  },
  'a2': {
    en: `## What happens after submitting my request?

After submission, your request goes through the following steps:

### 1. Acknowledgement of receipt
You receive an automatic confirmation as soon as your request is received. The status changes to **"Prices to be requested"**.

### 2. Requesting prices from carriers
SendWell requests prices from selected carriers suitable for your route and requirements.

### 3. Price quote
Once prices are received, SendWell prepares a **price quote**. You receive a notification via the messaging system.

### 4. Confirm order
Do you agree with the quote? Click **"Approve order"** in your portal. You will receive an order confirmation with all details.

### 5. Transport and tracking
After confirmation, the tracker is linked to your shipment. You can track the shipment live via the **Tracking** tab.

### Status explained

| Status | Meaning |
|--------|---------|
| Prices to be requested | Request received, carriers being contacted |
| Prices sent out | Price requests have been sent |
| Carrier prices received | Quote is being prepared |
| Price quoted | Quote ready, awaiting your approval |
| Order confirmed | Order confirmed, transport scheduled |
| Completed | Transport successfully completed |`,
    de: `## Was passiert nach dem Einreichen meiner Anfrage?

Nach der Einreichung durchläuft Ihre Anfrage folgende Schritte:

### 1. Eingangsbestätigung
Sie erhalten automatisch eine Bestätigung, sobald Ihre Anfrage eingegangen ist.

### 2. Preisanfrage bei Spediteuren
SendWell fragt Preise bei ausgewählten Spediteuren an, die für Ihre Route geeignet sind.

### 3. Preisangebot
Sobald die Preise eingegangen sind, erstellt SendWell ein **Angebot**. Sie erhalten eine Benachrichtigung.

### 4. Auftrag bestätigen
Sind Sie mit dem Angebot einverstanden? Klicken Sie auf **"Auftrag bestätigen"**. Sie erhalten eine Auftragsbestätigung mit allen Details.

### 5. Transport und Tracking
Nach Bestätigung wird der Tracker an Ihre Sendung gekoppelt. Sie können die Sendung live verfolgen.

### Status erklärt

| Status | Bedeutung |
|--------|-----------|
| Preise noch anzufragen | Anfrage erhalten, Spediteure werden kontaktiert |
| Preise versendet | Preisanfragen wurden versendet |
| Spediteurpreise eingegangen | Angebot wird erstellt |
| Preis angeboten | Angebot bereit, wartet auf Ihre Genehmigung |
| Auftrag | Auftrag bestätigt, Transport geplant |
| Abgeschlossen | Transport erfolgreich abgeschlossen |`,
    fr: `## Que se passe-t-il après avoir soumis ma demande ?

Après la soumission, votre demande passe par les étapes suivantes :

### 1. Accusé de réception
Vous recevez une confirmation automatique dès réception de votre demande.

### 2. Demande de prix aux transporteurs
SendWell demande des prix aux transporteurs sélectionnés adaptés à votre route.

### 3. Devis
Une fois les prix reçus, SendWell prépare un **devis**. Vous recevez une notification.

### 4. Confirmer la commande
Êtes-vous d\\'accord avec le devis ? Cliquez sur **"Approuver la commande"**. Vous recevrez une confirmation de commande avec tous les détails.

### 5. Transport et suivi
Après confirmation, le traceur est lié à votre expédition. Vous pouvez suivre l\\'expédition en direct.

### Explication des statuts

| Statut | Signification |
|--------|--------------|
| Prix à demander | Demande reçue, transporteurs contactés |
| Prix envoyés | Demandes de prix envoyées |
| Prix des transporteurs reçus | Devis en cours de préparation |
| Prix proposé | Devis prêt, en attente de votre approbation |
| Commande | Commande confirmée, transport planifié |
| Terminé | Transport réalisé avec succès |`,
  },
  'a3': {
    en: `## Modify or cancel a request

### Modifying
After submission, you **cannot modify a request yourself** via the portal. Contact us via the **messaging system** to request a change. SendWell will update the request for you.

### Cancelling
Do you want to cancel a request before the order is confirmed? Send a message via the portal with the request number and the reason. SendWell will process the cancellation as quickly as possible.

### After order confirmation
Has the order already been confirmed? Then the cancellation terms as stated in your agreement apply. Contact us to discuss the options.

> **Note:** Cancellation after order confirmation may result in cancellation fees.`,
    de: `## Anfrage ändern oder stornieren

### Ändern
Nach der Einreichung können Sie eine Anfrage **nicht selbst ändern**. Kontaktieren Sie uns über das **Nachrichtensystem**. SendWell passt die Anfrage für Sie an.

### Stornieren
Möchten Sie stornieren, bevor der Auftrag bestätigt ist? Senden Sie eine Nachricht mit der Anfragenummer und dem Grund. SendWell bearbeitet die Stornierung schnellstmöglich.

### Nach Auftragsbestätigung
Ist der Auftrag bereits bestätigt, gelten die Stornierungsbedingungen Ihres Vertrags. Kontaktieren Sie uns für weitere Informationen.

> **Hinweis:** Bei Stornierung nach Auftragsbestätigung können Stornierungsgebühren anfallen.`,
    fr: `## Modifier ou annuler une demande

### Modifier
Après la soumission, vous ne pouvez **pas modifier vous-même une demande** via le portail. Contactez-nous via le **système de messagerie**. SendWell mettra à jour la demande pour vous.

### Annuler
Vous souhaitez annuler avant la confirmation de la commande ? Envoyez un message avec le numéro de demande et la raison.

### Après confirmation de la commande
Si la commande est déjà confirmée, les conditions d\\'annulation de votre contrat s\\'appliquent.

> **Remarque :** L\\'annulation après confirmation de commande peut entraîner des frais d\\'annulation.`,
  },
  't1': {
    en: `## Live tracking of your shipment

SendWell uses GPS trackers placed on your shipment. Via the portal you can track the location in real time.

### Step 1 — Go to Track & Trace
Click **"Track & Trace"** in the menu. You will see a map with all active shipments.

### Step 2 — Select your shipment
In the left column you will see a list of your shipments. Click on a shipment to see the route on the map.

### What do you see?
- **Current location** — the last known position of the tracker
- **Route** — the path travelled from loading address to current point
- **Speed** — the current driving speed
- **Battery** — battery level of the tracker
- **Last update** — time of the most recent position

### How often is the position updated?
The tracker sends a new position every **30 to 40 seconds**. When stationary, updates are less frequent.

### No data visible?
- Check that the shipment has the status **"Order confirmed"**
- The tracker needs a GPS signal outdoors
- In tunnels or car parks the signal may temporarily drop out`,
    de: `## Live-Tracking Ihrer Sendung

SendWell verwendet GPS-Tracker, die an Ihrer Sendung befestigt werden. Über das Portal können Sie den Standort in Echtzeit verfolgen.

### Schritt 1 — Zum Tracking navigieren
Klicken Sie im Menü auf **"Track & Trace"**. Sie sehen eine Karte mit allen aktiven Sendungen.

### Schritt 2 — Sendung auswählen
In der linken Spalte sehen Sie eine Liste Ihrer Sendungen. Klicken Sie auf eine Sendung, um die Route auf der Karte zu sehen.

### Was sehen Sie?
- **Aktueller Standort** — letzte bekannte Position des Trackers
- **Route** — zurückgelegter Weg vom Ladeort bis zum aktuellen Punkt
- **Geschwindigkeit** — aktuelle Fahrgeschwindigkeit
- **Akku** — Akkustand des Trackers
- **Letzte Aktualisierung** — Zeitpunkt der letzten Position

### Keine Daten sichtbar?
- Prüfen Sie, ob die Sendung den Status **"Auftrag bestätigt"** hat
- Der Tracker benötigt im Freien ein GPS-Signal
- In Tunneln oder Parkhäusern kann das Signal kurzzeitig unterbrochen werden`,
    fr: `## Suivi en direct de votre expédition

SendWell utilise des traceurs GPS placés sur votre expédition. Via le portail, vous pouvez suivre la localisation en temps réel.

### Étape 1 — Aller au Track & Trace
Cliquez sur **"Track & Trace"** dans le menu. Vous verrez une carte avec toutes les expéditions actives.

### Étape 2 — Sélectionner votre expédition
Dans la colonne de gauche, vous verrez une liste de vos expéditions. Cliquez sur une expédition pour voir la route sur la carte.

### Que voyez-vous ?
- **Position actuelle** — dernière position connue du traceur
- **Route** — chemin parcouru du point de chargement au point actuel
- **Vitesse** — vitesse de conduite actuelle
- **Batterie** — niveau de batterie du traceur
- **Dernière mise à jour** — heure de la position la plus récente

### Aucune donnée visible ?
- Vérifiez que l\\'expédition a le statut **"Commande confirmée"**
- Le traceur a besoin d\\'un signal GPS en extérieur
- Dans les tunnels ou les parkings, le signal peut être temporairement perdu`,
  },
  't2': {
    en: `## View route history

In addition to live tracking, you can also view the complete route history of a shipment.

### How?
1. Open the **Track & Trace** tab
2. Click on the desired shipment
3. Click **"More info"** under the shipment details
4. Select a date range to see the route for that day

### What can you find?
- Exact route per day
- Intermediate stops
- Speed profile
- Battery profile

### How long is the history kept?
The route history is kept for the duration of the order plus **30 days** after completion.`,
    de: `## Routenverlauf anzeigen

Neben Live-Tracking können Sie auch den vollständigen Routenverlauf einer Sendung anzeigen.

### So geht es
1. Öffnen Sie das **Track & Trace**-Tab
2. Klicken Sie auf die gewünschte Sendung
3. Klicken Sie auf **"Mehr Info"**
4. Wählen Sie einen Datumsbereich

### Was können Sie finden?
- Genaue Route pro Tag
- Zwischenstopps
- Geschwindigkeitsverlauf
- Akkuverlauf

### Wie lange wird der Verlauf aufbewahrt?
Der Routenverlauf wird für die Dauer des Auftrags plus **30 Tage** nach Abschluss aufbewahrt.`,
    fr: `## Consulter l\\'historique des routes

En plus du suivi en direct, vous pouvez également consulter l\\'historique complet des routes d\\'une expédition.

### Comment ?
1. Ouvrez l\\'onglet **Track & Trace**
2. Cliquez sur l\\'expédition souhaitée
3. Cliquez sur **"Plus d\\'infos"**
4. Sélectionnez une plage de dates

### Que pouvez-vous trouver ?
- Route exacte par jour
- Arrêts intermédiaires
- Profil de vitesse
- Profil de batterie

### Combien de temps l\\'historique est-il conservé ?
L\\'historique des routes est conservé pendant la durée de la commande plus **30 jours** après l\\'achèvement.`,
  },
  'b1': {
    en: `## Sending messages via the portal

The messaging system is the fastest way to contact SendWell.

### Sending a message
1. Click **"Messages"** in the menu
2. Click **"New message"** or open an existing conversation
3. Type your message in the text box at the bottom
4. Press **Send** or use Enter

### Link to a shipment
You can link a message to a specific shipment. Select the shipment number from the dropdown menu above the text box. This keeps all communication organised by dossier.

### Attachments
You can also send **photos and images** via the paperclip icon. Useful for damage reports or questions about your goods.

### Response time
SendWell aims to respond within **4 hours** during office hours (Mon–Fri 08:00–17:30).

> **Tip:** For urgent matters you can also call us on **085 – 800 1843**.`,
    de: `## Nachrichten über das Portal senden

Das Nachrichtensystem ist der schnellste Weg, SendWell zu kontaktieren.

### Nachricht senden
1. Klicken Sie im Menü auf **"Nachrichten"**
2. Klicken Sie auf **"Neue Nachricht"**
3. Geben Sie Ihre Nachricht ein
4. Drücken Sie **Senden** oder Enter

### Mit einer Sendung verknüpfen
Sie können eine Nachricht mit einer bestimmten Sendung verknüpfen. Wählen Sie die Sendungsnummer im Dropdown-Menü aus.

### Anhänge
Sie können auch **Fotos und Bilder** über das Büroklammer-Symbol mitsenden.

### Reaktionszeit
SendWell strebt an, innerhalb von **4 Stunden** während der Bürozeiten (Mo–Fr 08:00–17:30) zu antworten.

> **Tipp:** Für dringende Angelegenheiten können Sie uns auch unter **085 – 800 1843** anrufen.`,
    fr: `## Envoyer des messages via le portail

Le système de messagerie est le moyen le plus rapide de contacter SendWell.

### Envoyer un message
1. Cliquez sur **"Messages"** dans le menu
2. Cliquez sur **"Nouveau message"**
3. Tapez votre message dans la zone de texte
4. Appuyez sur **Envoyer** ou Entrée

### Lier à une expédition
Vous pouvez lier un message à une expédition spécifique. Sélectionnez le numéro d\\'expédition dans le menu déroulant.

### Pièces jointes
Vous pouvez également envoyer des **photos et images** via l\\'icône trombone.

### Délai de réponse
SendWell vise à répondre dans les **4 heures** pendant les heures de bureau (lun–ven 08h00–17h30).

> **Conseil :** Pour les urgences, vous pouvez également nous appeler au **085 – 800 1843**.`,
  },
  'b2': {
    en: `## Notifications

### What are notifications?
SendWell sends you notifications for important updates, such as:
- A new price quote is ready
- Your shipment has reached its destination
- SendWell has responded to your message

### Where do I see notifications?
Notifications appear in the top right of the portal as a badge on the message icon.

### Email notifications
Important updates are also sent to your registered email address. Check your spam folder if you are not receiving emails.`,
    de: `## Benachrichtigungen

### Was sind Benachrichtigungen?
SendWell sendet Ihnen Benachrichtigungen bei wichtigen Updates, wie:
- Ein neues Preisangebot ist fertig
- Ihre Sendung hat ihr Ziel erreicht
- SendWell hat auf Ihre Nachricht geantwortet

### Wo sehe ich Benachrichtigungen?
Benachrichtigungen erscheinen oben rechts als Badge auf dem Nachrichten-Symbol.

### E-Mail-Benachrichtigungen
Wichtige Updates werden auch an Ihre registrierte E-Mail-Adresse gesendet.`,
    fr: `## Notifications

### Que sont les notifications ?
SendWell vous envoie des notifications pour les mises à jour importantes, comme :
- Un nouveau devis est prêt
- Votre expédition a atteint sa destination
- SendWell a répondu à votre message

### Où voir les notifications ?
Les notifications apparaissent en haut à droite du portail sous forme de badge sur l\\'icône de message.

### Notifications par email
Les mises à jour importantes sont également envoyées à votre adresse email enregistrée.`,
  },
  'd1': {
    en: `## Quotes and order confirmations

### Viewing a quote
1. Open the shipment for which you received a quote
2. Click on the **"Documents"** tab or scroll to the price overview
3. The quote shows the all-in price excluding VAT

### Order confirmation
After approving the quote, you receive an **order confirmation** by email. You can also find it in the portal:
1. Open the relevant shipment
2. Click on **"Order confirmation"**
3. You can print the document using the print button

### What is in the order confirmation?
- Loading and delivery address
- Date and time window
- Description of goods
- Agreed price
- Tracker information (if applicable)`,
    de: `## Angebote und Auftragsbestätigungen

### Angebot einsehen
1. Öffnen Sie die Sendung, für die Sie ein Angebot erhalten haben
2. Klicken Sie auf das **"Dokumente"**-Tab
3. Das Angebot zeigt den Gesamtpreis ohne MwSt.

### Auftragsbestätigung
Nach Genehmigung des Angebots erhalten Sie eine **Auftragsbestätigung** per E-Mail. Sie können diese auch im Portal finden.

### Was steht in der Auftragsbestätigung?
- Lade- und Lieferadresse
- Datum und Zeitfenster
- Warenbeschreibung
- Vereinbarter Preis
- Tracker-Informationen (falls zutreffend)`,
    fr: `## Devis et confirmations de commande

### Consulter un devis
1. Ouvrez l\\'expédition pour laquelle vous avez reçu un devis
2. Cliquez sur l\\'onglet **"Documents"**
3. Le devis affiche le prix tout compris hors TVA

### Confirmation de commande
Après approbation du devis, vous recevez une **confirmation de commande** par email.

### Que contient la confirmation de commande ?
- Adresse de chargement et de livraison
- Date et plage horaire
- Description des marchandises
- Prix convenu
- Informations sur le traceur (le cas échéant)`,
  },
  'd2': {
    en: `## Downloading and printing documents

### Printing
Every document in the portal has a **"Print"** button. Click it to open your browser's print dialog. You can also save as PDF via your printer settings.

### Downloading as PDF
Via the print function, select **"Save as PDF"** as the printer to save a digital copy.

### Documents not visible?
- Check that the shipment has the correct status
- Contact us via the messaging system if a document is missing`,
    de: `## Dokumente herunterladen und drucken

### Drucken
Jedes Dokument im Portal hat eine **"Drucken"**-Schaltfläche. Klicken Sie darauf, um den Druckdialog Ihres Browsers zu öffnen.

### Als PDF herunterladen
Über die Druckfunktion wählen Sie **"Als PDF speichern"** als Drucker.

### Dokumente nicht sichtbar?
- Prüfen Sie, ob die Sendung den richtigen Status hat
- Kontaktieren Sie uns über das Nachrichtensystem, wenn ein Dokument fehlt`,
    fr: `## Télécharger et imprimer des documents

### Imprimer
Chaque document dans le portail a un bouton **"Imprimer"**. Cliquez dessus pour ouvrir la boîte de dialogue d\\'impression.

### Télécharger en PDF
Via la fonction d\\'impression, choisissez **"Enregistrer au format PDF"**.

### Documents non visibles ?
- Vérifiez que l\\'expédition a le bon statut
- Contactez-nous via le système de messagerie si un document est manquant`,
  },
  'ac1': {
    en: `## Logging in to the SendWell portal

### Logging in
1. Go to **dashboard.sendwell.nl/portal**
2. Enter your **email address** and **password**
3. Click **"Log in"**

### Forgot your password?
Contact us via **info@sendwell.nl** or call **085 – 800 1843**. SendWell will send you a new password.

### First time logging in?
You will receive an email from SendWell with your login details as soon as your account is created. Check your spam folder if you have not received an email.

### Login problems?
- Check that you are using the correct email address
- Check that Caps Lock is off
- Clear your browser cache and try again
- Contact SendWell if the problem persists`,
    de: `## Anmeldung am SendWell-Portal

### Anmelden
1. Gehen Sie zu **dashboard.sendwell.nl/portal**
2. Geben Sie Ihre **E-Mail-Adresse** und Ihr **Passwort** ein
3. Klicken Sie auf **"Anmelden"**

### Passwort vergessen?
Kontaktieren Sie uns über **info@sendwell.nl** oder rufen Sie **085 – 800 1843** an.

### Zum ersten Mal anmelden?
Sie erhalten eine E-Mail von SendWell mit Ihren Anmeldedaten. Prüfen Sie Ihren Spam-Ordner, wenn Sie keine E-Mail erhalten haben.

### Anmeldeprobleme?
- Prüfen Sie, ob Sie die richtige E-Mail-Adresse verwenden
- Prüfen Sie, ob Caps Lock aktiviert ist
- Löschen Sie Ihren Browser-Cache und versuchen Sie es erneut`,
    fr: `## Connexion au portail SendWell

### Se connecter
1. Allez sur **dashboard.sendwell.nl/portal**
2. Entrez votre **adresse email** et votre **mot de passe**
3. Cliquez sur **"Se connecter"**

### Mot de passe oublié ?
Contactez-nous via **info@sendwell.nl** ou appelez le **085 – 800 1843**.

### Première connexion ?
Vous recevrez un email de SendWell avec vos identifiants. Vérifiez vos spams si vous n\\'avez pas reçu d\\'email.

### Problèmes de connexion ?
- Vérifiez que vous utilisez la bonne adresse email
- Vérifiez que Caps Lock est désactivé
- Videz le cache de votre navigateur et réessayez`,
  },
  'ac2': {
    en: `## Portal access for your company

### Requesting access
The portal is accessible to employees of your company who are registered with SendWell. Want to add additional users? Contact us via the messaging system or by email.

### What do other users see?
All users of your company see the same shipments and messages. There is no distinction in access levels.

### Logging out
Click on your name or the account icon in the top right and choose **"Log out"**. On shared computers we recommend always logging out after use.`,
    de: `## Portalzugang für Ihr Unternehmen

### Zugang beantragen
Das Portal ist für Mitarbeiter Ihres Unternehmens zugänglich, die bei SendWell registriert sind. Möchten Sie weitere Benutzer hinzufügen? Kontaktieren Sie uns.

### Was sehen andere Benutzer?
Alle Benutzer Ihres Unternehmens sehen dieselben Sendungen und Nachrichten.

### Abmelden
Klicken Sie oben rechts auf Ihren Namen und wählen Sie **"Abmelden"**. Auf gemeinsam genutzten Computern empfehlen wir, sich nach der Nutzung immer abzumelden.`,
    fr: `## Accès au portail pour votre entreprise

### Demander l\\'accès
Le portail est accessible aux employés de votre entreprise enregistrés chez SendWell. Vous souhaitez ajouter des utilisateurs supplémentaires ? Contactez-nous.

### Que voient les autres utilisateurs ?
Tous les utilisateurs de votre entreprise voient les mêmes expéditions et messages.

### Se déconnecter
Cliquez sur votre nom en haut à droite et choisissez **"Se déconnecter"**. Sur les ordinateurs partagés, nous recommandons de toujours se déconnecter après utilisation.`,
  },
};

const FAQ_VERTALINGEN = [
  { en_vraag: `How quickly does SendWell receive my request?`, en_antwoord: `Your request is received by SendWell immediately after submission. During office hours (Mon–Fri 08:00–17:30) you will typically receive a response within 4 hours.`,
    de_vraag: `Wie schnell erhält SendWell meine Anfrage?`, de_antwoord: `Ihre Anfrage wird sofort nach dem Absenden von SendWell empfangen. Während der Bürozeiten (Mo–Fr 08:00–17:30) erhalten Sie in der Regel innerhalb von 4 Stunden eine Antwort.`,
    fr_vraag: `Combien de temps met SendWell à recevoir ma demande ?`, fr_antwoord: `Votre demande est reçue immédiatement par SendWell après l\\'envoi. Pendant les heures de bureau (lun–ven 08h00–17h30), vous recevrez généralement une réponse dans les 4 heures.` },
  { en_vraag: `Can I track multiple shipments at the same time?`, en_antwoord: `Yes. In the Track & Trace overview you can see all active shipments on one map. You can zoom in and out on the route per shipment.`,
    de_vraag: `Kann ich mehrere Sendungen gleichzeitig verfolgen?`, de_antwoord: `Ja. Im Track & Trace-Übersicht sehen Sie alle aktiven Sendungen auf einer Karte.`,
    fr_vraag: `Puis-je suivre plusieurs expéditions en même temps ?`, fr_antwoord: `Oui. Dans la vue Track & Trace, vous pouvez voir toutes les expéditions actives sur une seule carte.` },
  { en_vraag: `What do I do if my shipment is damaged?`, en_antwoord: `Report damage as soon as possible via the messaging system. Add photos of the damaged goods and include the shipment number. SendWell will contact you to arrange follow-up.`,
    de_vraag: `Was tue ich bei Schäden an meiner Sendung?`, de_antwoord: `Melden Sie Schäden so schnell wie möglich über das Nachrichtensystem. Fügen Sie Fotos und die Sendungsnummer bei.`,
    fr_vraag: `Que faire si ma cargaison est endommagée ?`, fr_antwoord: `Signalez les dommages le plus tôt possible via le système de messagerie. Joignez des photos et incluez le numéro d\\'expédition.` },
  { en_vraag: `Does the portal work on my phone?`, en_antwoord: `Yes, the portal is fully responsive and works on smartphones and tablets. You only need an internet connection.`,
    de_vraag: `Funktioniert das Portal auf meinem Telefon?`, de_antwoord: `Ja, das Portal ist vollständig responsiv und funktioniert auf Smartphones und Tablets.`,
    fr_vraag: `Le portail fonctionne-t-il sur mon téléphone ?`, fr_antwoord: `Oui, le portail est entièrement responsive et fonctionne sur smartphones et tablettes.` },
  { en_vraag: `How do I know when my shipment has been delivered?`, en_antwoord: `You will receive a notification when the shipment is marked as "Completed". Via Track & Trace you can also see the last known location.`,
    de_vraag: `Wie weiß ich, wenn meine Sendung geliefert wurde?`, de_antwoord: `Sie erhalten eine Benachrichtigung, wenn die Sendung als "Abgeschlossen" markiert wird.`,
    fr_vraag: `Comment savoir quand mon expédition a été livrée ?`, fr_antwoord: `Vous recevrez une notification lorsque l'expédition sera marquée comme "Terminée".` },
  { en_vraag: `Can I attach a tracker directly to the driver?`, en_antwoord: `The tracker is placed on the shipment by SendWell before transport. You do not need to do anything yourself.`,
    de_vraag: `Kann ich den Tracker direkt dem Fahrer mitgeben?`, de_antwoord: `Der Tracker wird von SendWell vor dem Transport an der Sendung befestigt. Sie müssen nichts selbst tun.`,
    fr_vraag: `Puis-je donner le traceur directement au chauffeur ?`, fr_antwoord: `Le traceur est placé par SendWell sur l\\'expédition avant le transport. Vous n\\'avez rien à faire.` },
  { en_vraag: `What is the difference between groupage and full truck?`, en_antwoord: `Groupage means your goods share a truck with other shipments (cheaper but slightly slower). Full truck means the entire truck is reserved for your shipment (faster and more flexible).`,
    de_vraag: `Was ist der Unterschied zwischen Sammelladung und Vollladung?`, de_antwoord: `Sammelladung bedeutet, dass Ihre Waren einen LKW mit anderen Sendungen teilen. Vollladung bedeutet, dass der gesamte LKW für Ihre Sendung reserviert ist.`,
    fr_vraag: `Quelle est la différence entre groupage et chargement complet ?`, fr_antwoord: `Le groupage signifie que vos marchandises partagent un camion avec d\\'autres expéditions. Le chargement complet signifie que tout le camion est réservé pour votre expédition.` },
  { en_vraag: `How long before I receive a price quote?`, en_antwoord: `After submitting your request, you typically receive a price quote within a few hours during office hours.`,
    de_vraag: `Wie lange dauert es, bis ich ein Angebot erhalte?`, de_antwoord: `Nach dem Einreichen Ihrer Anfrage erhalten Sie während der Bürozeiten in der Regel innerhalb weniger Stunden ein Angebot.`,
    fr_vraag: `Combien de temps avant de recevoir un devis ?`, fr_antwoord: `Après avoir soumis votre demande, vous recevrez généralement un devis dans quelques heures pendant les heures de bureau.` },
  { en_vraag: `Can I send messages outside office hours?`, en_antwoord: `Yes, you can always send messages via the portal. SendWell will respond the next working day.`,
    de_vraag: `Kann ich auch außerhalb der Bürozeiten Nachrichten senden?`, de_antwoord: `Ja, Sie können jederzeit Nachrichten über das Portal senden. SendWell antwortet am nächsten Werktag.`,
    fr_vraag: `Puis-je envoyer des messages en dehors des heures de bureau ?`, fr_antwoord: `Oui, vous pouvez toujours envoyer des messages via le portail. SendWell répondra le prochain jour ouvrable.` },
  { en_vraag: `Is my data safe in the portal?`, en_antwoord: `Yes, the portal uses encrypted HTTPS connections and your data is stored securely on Dutch servers. Only authorised SendWell staff have access to your data.`,
    de_vraag: `Sind meine Daten im Portal sicher?`, de_antwoord: `Ja, das Portal verwendet verschlüsselte HTTPS-Verbindungen und Ihre Daten werden sicher auf niederländischen Servern gespeichert.`,
    fr_vraag: `Mes données sont-elles sécurisées dans le portail ?`, fr_antwoord: `Oui, le portail utilise des connexions HTTPS chiffrées et vos données sont stockées en toute sécurité sur des serveurs néerlandais.` },
];

const ARTIKEL_TITELS = {
  'a1': { en: 'Submit a new transport request', de: 'Eine neue Transportanfrage stellen', fr: 'Soumettre une nouvelle demande de transport' },
  'a2': { en: 'What happens after submitting my request?', de: 'Was passiert nach dem Einreichen?', fr: 'Que se passe-t-il après avoir soumis ma demande?' },
  'a3': { en: 'Can I modify or cancel a request?', de: 'Kann ich eine Anfrage ändern oder stornieren?', fr: 'Puis-je modifier ou annuler une demande?' },
  't1': { en: 'How do I track my shipment live?', de: 'Wie verfolge ich meine Sendung live?', fr: 'Comment suivre mon expédition en direct?' },
  't2': { en: 'View route history', de: 'Routenverlauf anzeigen', fr: "Consulter l'historique des routes" },
  'b1': { en: 'How do I send a message to SendWell?', de: 'Wie sende ich eine Nachricht?', fr: 'Comment envoyer un message à SendWell?' },
  'b2': { en: 'I received a notification — what now?', de: 'Ich habe eine Benachrichtigung erhalten', fr: 'Notification reçue — que faire?' },
  'd1': { en: 'Where can I find my quotes and confirmations?', de: 'Wo finde ich meine Angebote?', fr: 'Où trouver mes devis et confirmations?' },
  'd2': { en: 'How do I download or print a document?', de: 'Wie lade ich ein Dokument herunter?', fr: 'Comment télécharger ou imprimer un document?' },
  'ac1': { en: 'How do I log in to the portal?', de: 'Wie melde ich mich im Portal an?', fr: 'Comment me connecter au portail?' },
  'ac2': { en: 'Who can use the portal in my company?', de: 'Wer kann das Portal nutzen?', fr: 'Qui peut utiliser le portail?' },
};
const getArtikelTitel = (artikel, taal) => {
  if (!artikel || taal === 'nl' || !taal) return artikel?.titel || '';
  return (ARTIKEL_TITELS[artikel.id] || {})[taal] || artikel.titel || '';
};

const getArtikelInhoud = (artikel, taal) => {
  if (!artikel || taal === 'nl' || !taal) return artikel?.inhoud || '';
  return (INHOUD_VERTALINGEN[artikel.id] || {})[taal] || getArtikelInhoud(artikel, taal) || '';
};

const getFaqItems = (originals, taal) => {
  if (taal === 'nl' || !taal) return originals;
  return originals.map((item, i) => {
    const v = FAQ_VERTALINGEN[i];
    if (!v) return item;
    return {
      ...item,
      vraag: v[taal + '_vraag'] || item.vraag,
      antwoord: v[taal + '_antwoord'] || item.antwoord,
    };
  });
};

export default function CustomerHelpCenter() {
  const { t, taal } = useLanguage();
  const CATEGORIES = useMemo(() => getCategories(t), [t]);
  const [zoekterm, setZoekterm] = useState('');
  const [actieveCategorie, setActieveCategorie] = useState(null);
  const [actiefartikel, setActiefArtikel] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  // Zoekresultaten
  const zoekresultaten = useMemo(() => {
    if (!zoekterm.trim()) return [];
    const q = zoekterm.toLowerCase();
    return ARTIKELEN.filter(
      a =>
        a.titel.toLowerCase().includes(q) ||
        a.inhoud.toLowerCase().includes(q)
    ).slice(0, 6);
  }, [zoekterm]);

  const categorieArtikelen = useMemo(() => {
    if (!actieveCategorie) return [];
    return ARTIKELEN.filter(a => a.categorie === actieveCategorie);
  }, [actieveCategorie]);

  // Markdown-achtige renderer (simpel)
  const renderInhoud = (tekst) => {
    const regels = tekst.trim().split('\n');
    const elementen = [];
    let i = 0;

    while (i < regels.length) {
      const regel = regels[i];
      if (regel.startsWith('## ')) {
        elementen.push(<h2 key={i} style={{ fontSize: '20px', fontWeight: '700', color: '#1a364c', margin: '0 0 16px 0', paddingBottom: '8px', borderBottom: '2px solid #e2e8f0' }}>{regel.slice(3)}</h2>);
      } else if (regel.startsWith('### ')) {
        elementen.push(<h3 key={i} style={{ fontSize: '15px', fontWeight: '700', color: '#1a364c', margin: '20px 0 8px 0' }}>{regel.slice(4)}</h3>);
      } else if (regel.startsWith('> ')) {
        elementen.push(<div key={i} style={{ background: '#f0f7fa', borderLeft: '4px solid #1a364c', padding: '10px 14px', margin: '12px 0', borderRadius: '0 6px 6px 0', fontSize: '13px', color: '#374151' }}>{regel.slice(2)}</div>);
      } else if (regel.startsWith('| ') && regel.includes('|')) {
        // Tabel
        const tabelRegels = [];
        while (i < regels.length && regels[i].startsWith('|')) {
          tabelRegels.push(regels[i]);
          i++;
        }
        const headers = tabelRegels[0].split('|').filter(c => c.trim()).map(c => c.trim());
        const rows = tabelRegels.slice(2).map(r => r.split('|').filter(c => c.trim()).map(c => c.trim()));
        elementen.push(
          <div key={i} style={{ overflowX: 'auto', margin: '12px 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#1a364c', color: 'white' }}>
                  {headers.map((h, j) => <th key={j} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? 'white' : '#f8fafc' }}>
                    {row.map((cel, ci) => <td key={ci} style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>{cel}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      } else if (regel.startsWith('- ')) {
        const items = [];
        while (i < regels.length && regels[i].startsWith('- ')) {
          items.push(regels[i].slice(2));
          i++;
        }
        elementen.push(
          <ul key={`ul-${i}`} style={{ margin: '8px 0 12px 0', paddingLeft: '20px' }}>
            {items.map((item, j) => <li key={j} style={{ marginBottom: '4px', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>{item}</li>)}
          </ul>
        );
        continue;
      } else if (regel.trim() === '') {
        elementen.push(<div key={i} style={{ height: '8px' }} />);
      } else {
        // Inline bold
        const delen = regel.split(/\*\*(.*?)\*\*/g);
        const opgemaakt = delen.map((deel, j) =>
          j % 2 === 1
            ? <strong key={j} style={{ fontWeight: '700', color: '#1a364c' }}>{deel}</strong>
            : deel
        );
        elementen.push(<p key={i} style={{ fontSize: '14px', color: '#374151', lineHeight: '1.7', margin: '4px 0' }}>{opgemaakt}</p>);
      }
      i++;
    }
    return elementen;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* ── HERO ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1a364c 0%, #0e7490 100%)',
        padding: '48px 24px 64px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decoratieve cirkels */}
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '10%', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {t('klanten_portal')}
          </div>
          <h1 style={{ color: 'white', fontSize: '32px', fontWeight: '800', margin: '0 0 8px 0', lineHeight: '1.2' }}>
            {t('help_hero_titel') || 'How can we help you?'}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '15px', margin: '0 0 28px 0' }}>
            {t('help_hero_sub') || 'Search our knowledge base or browse the categories.'}
          </p>

          {/* Zoekbalk */}
          <div style={{ position: 'relative', maxWidth: '560px' }}>
            <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', pointerEvents: 'none' }}>🔍</div>
            <input
              type="text"
              placeholder={t('help_zoek_placeholder') || 'Search by topic...'}
              value={zoekterm}
              onChange={e => { setZoekterm(e.target.value); setActieveCategorie(null); setActiefArtikel(null); }}
              style={{
                width: '100%',
                padding: '14px 16px 14px 48px',
                fontSize: '15px',
                border: 'none',
                borderRadius: '10px',
                outline: 'none',
                background: 'white',
                boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                boxSizing: 'border-box',
              }}
            />
            {zoekterm && (
              <button
                onClick={() => setZoekterm('')}
                style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#9ca3af' }}
              >✕</button>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px 48px' }}>

        {/* ── ZOEKRESULTATEN ── */}
        {zoekterm && (
          <div style={{ marginTop: '-20px' }}>
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a364c' }}>
                  {zoekresultaten.length} resultaat{zoekresultaten.length !== 1 ? 'en' : ''} voor "{zoekterm}"
                </span>
              </div>
              {zoekresultaten.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>{t('geen_resultaten')}</div>
                  <div style={{ fontSize: '13px' }}>{t('help_geen_resultaten') || 'Probeer andere zoektermen'} Berichten.</div>
                </div>
              ) : (
                zoekresultaten.map(a => {
                  const cat = CATEGORIES.find(c => c.id === a.categorie);
                  return (
                    <div
                      key={a.id}
                      onClick={() => { setActiefArtikel(a); setZoekterm(''); }}
                      style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <span style={{ fontSize: '20px' }}>{cat?.icon}</span>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a364c' }}>{getArtikelTitel(a, taal)}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{cat?.titel}</div>
                      </div>
                      <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: '16px' }}>→</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── ARTIKEL WEERGAVE ── */}
        {actiefartikel && !zoekterm && (
          <div style={{ marginTop: '32px' }}>
            <button
              onClick={() => setActiefArtikel(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a364c', fontSize: '14px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px', padding: 0 }}
            >
              {t('terug') || '← Back to overview'}
            </button>
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', padding: '32px' }}>
              {renderInhoud(getArtikelInhoud(actiefartikel, taal))}
              <div style={{ marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#9ca3af' }}>{t('help_nuttig') || 'Was dit artikel behulpzaam?'}</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={{ padding: '6px 16px', background: '#f0fdf4', color: '#166534', border: '1px solid #86efac', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>{t('ja_btn') || '👍 Yes'}</button>
                  <button style={{ padding: '6px 16px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>{t('nee_btn') || '👎 No'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── CATEGORIE ARTIKELEN ── */}
        {actieveCategorie && !actiefartikel && !zoekterm && (
          <div style={{ marginTop: '32px' }}>
            <button
              onClick={() => setActieveCategorie(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a364c', fontSize: '14px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px', padding: 0 }}
            >
              {t('terug') || '← All categories'}
            </button>
            {actieveCategorie === 'faq' ? (
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1a364c', margin: '0 0 20px 0' }}>{t('help_faq') || '❓ Frequently asked questions'}</h2>
                {getFaqItems(FAQ_ITEMS, taal).map((item, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: '10px', marginBottom: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      style={{ width: '100%', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}
                    >
                      <span style={{ fontWeight: '600', fontSize: '14px', color: '#1a364c', paddingRight: '16px' }}>{item.vraag}</span>
                      <span style={{ fontSize: '18px', color: '#1a364c', flexShrink: 0, transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(180deg)' : 'none' }}>▾</span>
                    </button>
                    {openFaq === i && (
                      <div style={{ padding: '0 20px 16px', fontSize: '14px', color: '#374151', lineHeight: '1.7', borderTop: '1px solid #f1f5f9' }}>
                        <div style={{ paddingTop: '12px' }}>{item.antwoord}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {(() => {
                  const cat = CATEGORIES.find(c => c.id === actieveCategorie);
                  return <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1a364c', margin: '0 0 20px 0' }}>{cat?.icon} {cat?.titel}</h2>;
                })()}
                {categorieArtikelen.map(a => (
                  <div
                    key={a.id}
                    onClick={() => setActiefArtikel(a)}
                    style={{ background: 'white', borderRadius: '10px', padding: '16px 20px', marginBottom: '8px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'box-shadow 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'}
                  >
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a364c' }}>{getArtikelTitel(a, taal)}</div>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '18px', marginLeft: '16px' }}>→</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CATEGORIEËN GRID ── */}
        {!zoekterm && !actieveCategorie && !actiefartikel && (
          <>
            <div style={{ marginTop: '40px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1a364c', margin: '0 0 16px 0' }}>{t('help_bladeren') || 'Browse by category'}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                {CATEGORIES.map(cat => (
                  <div
                    key={cat.id}
                    onClick={() => setActieveCategorie(cat.id)}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      borderLeft: `4px solid ${cat.kleur}`,
                      transition: 'transform 0.15s, box-shadow 0.15s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '14px',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                  >
                    <div style={{ fontSize: '28px', lineHeight: '1', flexShrink: 0 }}>{cat.icon}</div>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '14px', color: '#1a364c', marginBottom: '4px' }}>{cat.titel}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>{cat.omschrijving}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── POPULAIRE ARTIKELEN ── */}
            <div style={{ marginTop: '40px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1a364c', margin: '0 0 16px 0' }}>{t('help_populair') || '📌 Popular articles'}</h2>
              <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                {[ARTIKELEN[0], ARTIKELEN[1], ARTIKELEN[3], ARTIKELEN[5]].map((a, i, arr) => {
                  const cat = CATEGORIES.find(c => c.id === a.categorie);
                  return (
                    <div
                      key={a.id}
                      onClick={() => setActiefArtikel(a)}
                      style={{
                        padding: '14px 20px',
                        borderBottom: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <span style={{ fontSize: '18px' }}>{cat?.icon}</span>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500', flex: 1 }}>{getArtikelTitel(a, taal)}</span>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>→</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── CONTACT CTA ── */}
            <div style={{
              marginTop: '40px',
              background: 'linear-gradient(135deg, #1a364c 0%, #0e7490 100%)',
              borderRadius: '16px',
              padding: '28px 32px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
            }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '17px', color: 'white', marginBottom: '4px' }}>{t('help_niet_gevonden') || 'Antwoord niet gevonden?'}</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>{t('help_contact') || 'Neem contact op met SendWell'}</div>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📞</span>
                  <span style={{ color: 'white', fontSize: '13px', fontWeight: '600' }}>085 – 800 1843</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✉️</span>
                  <span style={{ color: 'white', fontSize: '13px', fontWeight: '600' }}>info@sendwell.nl</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
